// Мок CRM заявок (04.10.2026): карточка, контакты, касания, задачи, файлы,
// ручное заведение, загрузка и выгрузка базы — всё в памяти вкладки.
//
// Саму заявку мок берёт с доски (`api.applications.board()`), какой бы она ни
// была: на стенде доска — живой шлюз, а CRM — этот мок
// (BUN_PUBLIC_MOCK_DOMAINS=applicationCrm), в офлайне обе на моке. Поэтому
// карточка собирается вокруг той же заявки, что видна на доске, а CRM-данные
// лежат здесь по её id. Перезагрузка страницы сбрасывает всё, как у остальных
// моков. Данные выдуманные и живут только здесь.
import {
	CRM_FILE_MAX_BYTES,
	CRM_LEAD_SOURCE_LABELS,
	type CrmActivity,
	type CrmBoardExtras,
	type CrmCompany,
	type CrmContact,
	type CrmDuplicate,
	type CrmFile,
	type CrmFileCategory,
	type CrmImportField,
	type CrmLeadAccount,
	type CrmLeadCreate,
	type CrmLeadImportJob,
	type CrmLeadImportResult,
	type CrmLeadProfile,
	type CrmLinks,
	type CrmPerson,
	type CrmTask,
	type CrmTaskDueFilter,
	type CrmTaskWithLead,
	type CrmTimelineItem,
} from "@osmix/contracts";
import { sleep } from "@/lib/utils";

import { api } from "../client";
import { ApiError } from "../errors";
import type { Api, ApplicationCard, ApplicationStatus, SellerApplication } from "../types";
import { mockInsertApplication, mockPatchApplication } from "./applications";

// ── Время ────────────────────────────────────────────────────────────────

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const iso = (ms: number) => new Date(ms).toISOString();
const ago = (days: number, hours = 0) => iso(Date.now() - days * DAY - hours * HOUR);
/** «Через N дней в ЧЧ:ММ» по часам браузера — так менеджер и ставит сроки. */
const dayAt = (days: number, hours: number, minutes = 0) => {
	const date = new Date();
	date.setDate(date.getDate() + days);
	date.setHours(hours, minutes, 0, 0);
	return date.toISOString();
};
const startOfToday = () => {
	const date = new Date();
	date.setHours(0, 0, 0, 0);
	return date.getTime();
};

let seq = 0;
const nextId = (prefix: string) => `${prefix}_${(++seq).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// ── Кто действует ───────────────────────────────────────────────────────

const initialsOf = (name: string) =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("") || "?";

type Me = CrmPerson & { isAdmin: boolean };

/**
 * Текущий сотрудник — из сохранённой сессии консоли. Стор не импортируем:
 * он сам зависит от `api`, и мок замкнулся бы в кольцо при загрузке.
 */
const me = (): Me => {
	try {
		const raw = globalThis.localStorage?.getItem("osmix-admin-auth");
		const user = raw
			? (JSON.parse(raw) as { state?: { user?: { id?: string; name?: string; role?: string } } }).state?.user
			: undefined;
		if (user?.id) {
			const name = user.name || "Менеджер";
			return { id: user.id, name, initials: initialsOf(name), isAdmin: user.role === "admin" };
		}
	} catch {
		// приватный режим или битое значение — ниже запасной сотрудник
	}
	return { id: "usr_seed_admin", name: "Платформа", initials: "П", isAdmin: true };
};
const personOf = (who: Me): CrmPerson => ({ id: who.id, name: who.name, initials: who.initials });

// Те же менеджеры, что на мок-доске.
const ORLOVA: CrmPerson = { id: "mgr_orlova", name: "Ирина Орлова", initials: "ИО" };
const PETROV: CrmPerson = { id: "mgr_petrov", name: "Дмитрий Петров", initials: "ДП" };
const SOKOLOVA: CrmPerson = { id: "mgr_sokolova", name: "Мария Соколова", initials: "МС" };

// ── Заглушки файлов ─────────────────────────────────────────────────────

const svgPhoto = (label: string, hue: number) =>
	new Blob(
		[
			`<svg xmlns="http://www.w3.org/2000/svg" width="960" height="720" viewBox="0 0 960 720"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="hsl(${hue} 42% 82%)"/><stop offset="1" stop-color="hsl(${(hue + 35) % 360} 38% 56%)"/></linearGradient></defs><rect width="960" height="720" fill="url(#g)"/><circle cx="480" cy="370" r="190" fill="rgba(255,255,255,.3)"/><text x="480" y="385" font-family="sans-serif" font-size="40" fill="rgba(0,0,0,.55)" text-anchor="middle">${label}</text></svg>`,
		],
		{ type: "image/svg+xml" },
	);

/** «Фото товара»: настоящий JPEG с подписью, чтобы превью и просмотр работали как с живым файлом. */
const photo = (label: string, hue: number) => async (): Promise<Blob> => {
	if (typeof document === "undefined") return svgPhoto(label, hue);
	const canvas = document.createElement("canvas");
	canvas.width = 960;
	canvas.height = 720;
	const ctx = canvas.getContext("2d");
	if (!ctx) return svgPhoto(label, hue);
	const gradient = ctx.createLinearGradient(0, 0, 960, 720);
	gradient.addColorStop(0, `hsl(${hue} 42% 82%)`);
	gradient.addColorStop(1, `hsl(${(hue + 35) % 360} 38% 56%)`);
	ctx.fillStyle = gradient;
	ctx.fillRect(0, 0, 960, 720);
	ctx.fillStyle = "rgba(255,255,255,0.3)";
	ctx.beginPath();
	ctx.arc(480, 370, 190, 0, Math.PI * 2);
	ctx.fill();
	ctx.fillStyle = "rgba(0,0,0,0.55)";
	ctx.textAlign = "center";
	ctx.font = "600 40px system-ui, sans-serif";
	ctx.fillText(label, 480, 380);
	ctx.font = "22px system-ui, sans-serif";
	ctx.fillText("демо-фото мока", 480, 420);
	return new Promise((resolve) =>
		canvas.toBlob((blob) => resolve(blob ?? svgPhoto(label, hue)), "image/jpeg", 0.85),
	);
};

/** Одностраничный PDF. Встроенный шрифт PDF кириллицу не знает — текст латиницей. */
const pdf = (lines: string[]) => async (): Promise<Blob> => {
	const text = lines
		.map(
			(line, i) =>
				`BT /F1 ${i === 0 ? 20 : 12} Tf 56 ${780 - i * 26} Td (${line.replace(/[()\\]/g, "\\$&")}) Tj ET`,
		)
		.join("\n");
	const objects = [
		"<< /Type /Catalog /Pages 2 0 R >>",
		"<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
		"<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>",
		`<< /Length ${text.length} >>\nstream\n${text}\nendstream`,
		"<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
	];
	let out = "%PDF-1.4\n";
	const offsets: number[] = [];
	objects.forEach((body, i) => {
		offsets.push(out.length);
		out += `${i + 1} 0 obj\n${body}\nendobj\n`;
	});
	const xref = out.length;
	out += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
	out += offsets.map((offset) => `${String(offset).padStart(10, "0")} 00000 n \n`).join("");
	out += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
	return new Blob([out], { type: "application/pdf" });
};

const csv = (rows: string[][]) => async (): Promise<Blob> =>
	new Blob([`﻿${rows.map((row) => row.join(";")).join("\r\n")}`], { type: "text/csv" });

// ── Хранилище ───────────────────────────────────────────────────────────

type StoredTask = Omit<CrmTask, "overdue">;
type StoredFile = CrmFile & { content: Blob | (() => Promise<Blob>) };

type Lead = {
	profile: CrmLeadProfile;
	contacts: CrmContact[];
	activities: CrmActivity[];
	tasks: StoredTask[];
	files: StoredFile[];
	/** Правки основных полей (название, телефон…): на живой доске мок их не запишет. */
	overrides: Partial<SellerApplication>;
	account: CrmLeadAccount | null;
	importJobs: CrmLeadImportJob[];
};

const leads = new Map<string, Lead>();
/** Заявки, заведённые в моке: на живой доске шлюза их нет, карточка берёт их отсюда. */
const created = new Map<string, SellerApplication>();

const blankProfile = (over: Partial<CrmLeadProfile> = {}): CrmLeadProfile => ({
	source: "landing",
	sourceDetail: "",
	company: {},
	links: {},
	tags: [],
	assortmentSize: null,
	avgCheck: null,
	monthlyPotential: null,
	comment: "",
	...over,
});

const contact = (
	name: string,
	role: string,
	phone: string,
	opts: { email?: string; telegram?: string; primary?: boolean; createdAt?: string } = {},
): CrmContact => ({
	id: nextId("cct"),
	name,
	role,
	phone,
	email: opts.email ?? "",
	telegram: opts.telegram ?? "",
	isPrimary: opts.primary ?? false,
	createdAt: opts.createdAt ?? ago(10),
});

const touch = (
	kind: CrmActivity["kind"],
	body: string,
	occurredAt: string,
	author: CrmPerson,
	outcome: CrmActivity["outcome"] = null,
	fileIds: string[] = [],
): CrmActivity => ({
	id: nextId("act"),
	kind,
	body,
	outcome,
	occurredAt,
	author,
	createdAt: occurredAt,
	editedAt: null,
	fileIds,
});

const task = (
	applicationId: string,
	title: string,
	kind: CrmTask["kind"],
	dueAt: string | null,
	assignee: CrmPerson | null,
	opts: { createdAt?: string; createdBy?: CrmPerson; doneAt?: string; doneBy?: CrmPerson } = {},
): StoredTask => ({
	id: nextId("tsk"),
	applicationId,
	title,
	kind,
	dueAt,
	assignee,
	doneAt: opts.doneAt ?? null,
	doneBy: opts.doneAt ? (opts.doneBy ?? assignee) : null,
	createdAt: opts.createdAt ?? ago(3),
	createdBy: opts.createdBy ?? assignee,
});

const file = (
	applicationId: string,
	filename: string,
	mime: string,
	category: CrmFileCategory,
	sizeBytes: number,
	createdAt: string,
	uploadedBy: CrmPerson | null,
	content: StoredFile["content"],
): StoredFile => {
	const id = nextId("fil");
	return {
		id,
		applicationId,
		filename,
		mime,
		sizeBytes,
		category,
		isImage: mime.startsWith("image/"),
		uploadedBy,
		createdAt,
		downloadPath: `/admin/applications/${applicationId}/files/${id}`,
		content,
	};
};

const lead = (over: Partial<Lead>): Lead => ({
	profile: blankProfile(),
	contacts: [],
	activities: [],
	tasks: [],
	files: [],
	overrides: {},
	account: null,
	importJobs: [],
	...over,
});

// ── Демо-данные для заявок мок-доски ────────────────────────────────────
// Половина задач — на текущем сотруднике, чтобы «Мои задачи» были непустыми
// под любым входом.

const SEEDS: Record<string, (self: CrmPerson) => Lead> = {
	app_0001: (self) =>
		lead({
			profile: blankProfile({
				links: { instagram: "goncharnaya_spb", vk: "vk.com/goncharnaya_spb" },
				tags: ["ручная работа"],
			}),
			contacts: [contact("Ольга Ким", "Владелица мастерской", "+79214451208", { primary: true, createdAt: ago(1) })],
			tasks: [
				task("app_0001", "Первый звонок: знакомство, ассортимент, где продают", "call", dayAt(0, 18), self, {
					createdAt: ago(1),
				}),
			],
		}),
	app_0002: (self) => {
		const priceList = file(
			"app_0002",
			"Прайс серебро и камень 2026.csv",
			"text/csv",
			"price_list",
			18_432,
			ago(3, 2),
			self,
			csv([
				["Артикул", "Название", "Цена, руб.", "Остаток"],
				["SK-101", "Кольцо «Малахит», серебро 925", "6 400", "12"],
				["SK-118", "Серьги «Чароит»", "5 200", "8"],
				["SK-204", "Подвеска «Яшма уральская»", "3 900", "21"],
			]),
		);
		const shot1 = file("app_0002", "Витрина на выставке 1.jpg", "image/jpeg", "photos", 412_008, ago(3, 1), self, photo("Витрина 1", 200));
		const shot2 = file("app_0002", "Витрина на выставке 2.jpg", "image/jpeg", "photos", 398_114, ago(3, 1), self, photo("Витрина 2", 170));
		const shot3 = file("app_0002", "Кольцо Малахит.jpg", "image/jpeg", "photos", 287_440, ago(3, 1), self, photo("Малахит", 140));
		return lead({
			profile: blankProfile({
				source: "exhibition",
				sourceDetail: "Выставка «Ремесленная Россия», сентябрь 2026",
				company: { inn: "667012345678" },
				links: {
					website: "serebro-kamen.ru",
					wildberries: "https://www.wildberries.ru/seller/1234567",
					ozon: "https://www.ozon.ru/seller/serebro-i-kamen-765432/",
				},
				tags: ["крупный ассортимент", "есть на WB"],
				assortmentSize: 640,
				avgCheck: 4800,
				monthlyPotential: 900_000,
				comment: "Крупный ассортимент, просит ускорить проверку.",
			}),
			contacts: [
				contact("Артём Власов", "Владелец", "+79126003371", {
					email: "vlasov@serebro-kamen.ru",
					telegram: "vlasov_silver",
					primary: true,
					createdAt: ago(4),
				}),
				contact("Ксения Власова", "Маркетплейсы и отгрузки", "+79126003372", { createdAt: ago(3) }),
			],
			activities: [
				touch("call", "Не взял трубку, отправили сообщение в WhatsApp.", ago(3, 3), self, "no_answer"),
				touch(
					"message",
					"Прислал прайс и фото витрины с выставки. Хочет начать с колец и серёг.",
					ago(3, 1),
					self,
					null,
					[priceList.id, shot1.id, shot2.id, shot3.id],
				),
			],
			tasks: [
				task("app_0002", "Перезвонить: условия комиссии и сроки проверки", "call", dayAt(-2, 12), self, {
					createdAt: ago(3),
				}),
				task("app_0002", "Отправить договор-оферту", "send_offer", dayAt(2, 11), self, { createdAt: ago(3) }),
			],
			files: [priceList, shot1, shot2, shot3],
		});
	},
	app_0003: (self) => {
		const catalog = file(
			"app_0003",
			"Каталог Льняной Дом 2026.pdf",
			"application/pdf",
			"catalog",
			2_418_330,
			ago(5),
			ORLOVA,
			pdf(["Lnyanoy Dom - catalog 2026", "Tablecloths, napkins, bed linen", "OSMIX mock file"]),
		);
		const card = file(
			"app_0003",
			"Карточка предприятия.pdf",
			"application/pdf",
			"documents",
			96_220,
			ago(4),
			ORLOVA,
			pdf(["Company details", "INN 3702123456 / KPP 370201001", "OSMIX mock file"]),
		);
		const linen = file("app_0003", "Скатерть лён.jpg", "image/jpeg", "photos", 356_900, ago(5), ORLOVA, photo("Скатерть", 35));
		const towels = file("app_0003", "Полотенца вафельные.jpg", "image/jpeg", "photos", 301_550, ago(5), ORLOVA, photo("Полотенца", 55));
		return lead({
			profile: blankProfile({
				source: "referral",
				sourceDetail: "Порекомендовала «Мастерская Анны»",
				company: {
					inn: "3702123456",
					kpp: "370201001",
					ogrn: "1153702001234",
					shortName: "ООО «ЛЬНЯНОЙ ДОМ»",
					fullName: "ОБЩЕСТВО С ОГРАНИЧЕННОЙ ОТВЕТСТВЕННОСТЬЮ «ЛЬНЯНОЙ ДОМ»",
					orgForm: "ООО",
					address: "153002, Ивановская обл., г. Иваново, пр-кт Ленина, д. 57, оф. 12",
					director: "Гущина Наталья Викторовна",
					state: "Действующая",
					registeredAt: "2015-03-18",
					okved: "13.92 Производство готовых текстильных изделий, кроме одежды",
					filledFrom: "dadata",
					filledAt: ago(5),
				},
				links: {
					website: "lnyanoy-dom.ru",
					wildberries: "https://www.wildberries.ru/seller/2345678",
					telegram: "t.me/lnyanoydom",
				},
				tags: ["текстиль", "есть на WB"],
				assortmentSize: 180,
				avgCheck: 3200,
				monthlyPotential: 450_000,
			}),
			contacts: [
				contact("Наталья Гущина", "Директор, собственник", "+79203415519", {
					email: "info@lnyanoy-dom.ru",
					primary: true,
					createdAt: ago(6),
				}),
				contact("Сергей Гущин", "Склад и отгрузки", "+79203415520", { telegram: "s_gushchin", createdAt: ago(5) }),
			],
			activities: [
				touch(
					"call",
					"Познакомились. Шьют сами, 180 позиций, продают на WB. Интересно попробовать нишевую площадку, просили условия письмом.",
					ago(5, 4),
					ORLOVA,
					"interested",
					[],
				),
				touch("email", "Отправили презентацию площадки, условия комиссии и инструкцию по кабинету.", ago(4, 2), ORLOVA, null, [card.id]),
				touch("call", "Читают договор, вернутся с вопросами по логистике до конца недели.", ago(1, 3), self, "thinking"),
			],
			tasks: [
				task("app_0003", "Отправить договор и ответить по логистике", "send_offer", dayAt(1, 11), self, {
					createdAt: ago(1, 3),
				}),
				task("app_0003", "Отправить презентацию и условия", "email", ago(4, 3), ORLOVA, {
					createdAt: ago(5, 4),
					doneAt: ago(4, 2),
				}),
			],
			files: [catalog, card, linen, towels],
		});
	},
	app_0004: () =>
		lead({
			profile: blankProfile({
				source: "cold_base",
				sourceDetail: "База ярмарки мастеров, сентябрь",
				links: { avito: "https://www.avito.ru/user/vyazanyi_mir" },
				tags: ["холодная база"],
			}),
			contacts: [contact("Елена Дроздова", "", "+79172280463", { primary: true, createdAt: ago(9) })],
			activities: [
				touch("call", "", ago(8, 2), PETROV, "no_answer"),
				touch("call", "Попросила перезвонить в понедельник после обеда.", ago(6, 5), PETROV, "callback"),
			],
			tasks: [task("app_0004", "Перезвонить после обеда", "call", dayAt(0, 15, 30), PETROV, { createdAt: ago(6, 5) })],
		}),
	app_0005: (self) =>
		lead({
			profile: blankProfile({
				source: "marketplace_scout",
				sourceDetail: "Нашли на Ozon, рейтинг 4,8",
				links: { ozon: "https://www.ozon.ru/seller/derevo-i-smola-112233/", instagram: "@derevo.smola" },
				tags: ["декор", "нужна помощь с каталогом"],
				assortmentSize: 60,
				avgCheck: 5600,
				monthlyPotential: 300_000,
			}),
			contacts: [
				contact("Павел Ершов", "Мастер, владелец", "+79137702145", {
					email: "pavel@derevo-smola.ru",
					primary: true,
					createdAt: ago(12),
				}),
			],
			activities: [
				touch("meeting", "Созвонились в Zoom, показали кабинет. Каталог в таблице Ozon, фото на диске.", ago(4), ORLOVA, "interested"),
			],
			tasks: [
				task("app_0005", "Загрузить каталог из выгрузки Ozon через ИИ", "upload_catalog", dayAt(3, 12), self, {
					createdAt: ago(4),
				}),
			],
			account: {
				userId: "usr_seller_derevo",
				email: "pavel@derevo-smola.ru",
				shopId: "usr_seller_derevo",
				shopName: "Дерево и Смола",
				onboardingStatus: "profile_pending",
				productsCount: 24,
				publishedProductsCount: 0,
			},
			importJobs: [
				{
					id: "cij_derevo_1",
					title: "Выгрузка Ozon и фото с диска",
					status: "review",
					createdAt: ago(2),
					itemsTotal: 24,
					itemsCommitted: 0,
				},
			],
		}),
	app_0006: () =>
		lead({
			profile: blankProfile({
				source: "social",
				sourceDetail: "Instagram, отметка в сторис",
				links: { instagram: "bumazhnaya.ptica" },
				tags: ["бумага", "декор"],
				comment: "Не приложены документы ИП.",
			}),
			contacts: [contact("Софья Лебедева", "", "+79031186290", { primary: true, createdAt: ago(15) })],
			activities: [touch("comment", "Ждём документы ИП, обещала до пятницы.", ago(7), ORLOVA)],
		}),
	app_0007: (self) =>
		lead({
			profile: blankProfile({
				source: "landing",
				company: { inn: "332901234561" },
				links: { website: "steklodyv.ru" },
				tags: ["стекло"],
				assortmentSize: 90,
				avgCheck: 2900,
			}),
			contacts: [contact("Игорь Мельник", "Стеклодув, ИП", "+79106754012", { primary: true, createdAt: ago(18) })],
			activities: [touch("comment", "Документы на проверке, вопросов нет.", ago(2), SOKOLOVA)],
			tasks: [task("app_0007", "Проверить документы ИП", "other", dayAt(0, 17), SOKOLOVA, { createdAt: ago(2) })],
		}),
	app_0008: (self) =>
		lead({
			profile: blankProfile({ source: "landing", tags: ["керамика"] }),
			contacts: [contact("Анна Родина", "", "+79184027735", { primary: true, createdAt: ago(21) })],
			activities: [touch("call", "Объяснили замечания модерации по фото товаров.", ago(6), SOKOLOVA, "reached")],
			tasks: [
				task("app_0008", "Созвониться по замечаниям модерации", "call", dayAt(-1, 16), self, { createdAt: ago(6) }),
			],
		}),
	app_0009: () =>
		lead({
			profile: blankProfile({
				source: "referral",
				sourceDetail: "Знакомая основателя",
				links: { website: "masterskaya-anny.ru", instagram: "masterskaya.anny" },
				tags: ["украшения", "амбассадор"],
				assortmentSize: 48,
				avgCheck: 3500,
				monthlyPotential: 250_000,
			}),
			contacts: [
				contact("Анна Мастерова", "Владелица", "+79115032864", {
					email: "anna@masterskaya-anny.ru",
					primary: true,
					createdAt: ago(30),
				}),
			],
			activities: [touch("meeting", "Запуск магазина: проверили витрину вместе.", ago(11), ORLOVA, "reached")],
			account: {
				userId: "usr_seller_anna",
				email: "anna@masterskaya-anny.ru",
				shopId: "shop_masterskaya_anny",
				shopName: "Мастерская Анны",
				onboardingStatus: "active",
				productsCount: 48,
				publishedProductsCount: 45,
			},
			importJobs: [
				{
					id: "cij_anna_1",
					title: "Каталог из прайса и фото",
					status: "done",
					createdAt: ago(12),
					itemsTotal: 48,
					itemsCommitted: 48,
				},
			],
		}),
	app_0010: () =>
		lead({
			profile: blankProfile({ source: "landing", tags: ["текстиль"] }),
			contacts: [contact("Вера Соловьёва", "", "+79200169347", { primary: true, createdAt: ago(34) })],
		}),
	app_0011: () =>
		lead({
			profile: blankProfile({ source: "cold_base", tags: ["перепродажа"], comment: "Перепродажа фабричного товара." }),
			contacts: [contact("Роман Гаврилов", "", "+79273901582", { primary: true, createdAt: ago(26) })],
		}),
	app_0012: () =>
		lead({
			profile: blankProfile({ source: "landing" }),
			contacts: [contact("Артём Власов", "", "+79126003371", { primary: true, createdAt: ago(120) })],
		}),
};

let seeded = false;
const ensureSeeded = () => {
	if (seeded) return;
	seeded = true;
	const self = personOf(me());
	for (const [id, build] of Object.entries(SEEDS)) leads.set(id, build(self));
};

/** CRM-данные заявки; для заявки без них — собираются из самой заявки. */
const leadFor = (app: SellerApplication): Lead => {
	ensureSeeded();
	let entry = leads.get(app.id);
	if (!entry) {
		entry = lead({
			profile: blankProfile({
				source: app.source ?? "landing",
				comment: app.note ?? "",
				company: app.inn ? { inn: app.inn } : {},
			}),
			contacts: [
				contact(app.contactName, "", app.phone ?? "", {
					email: app.email ?? "",
					primary: true,
					createdAt: app.submittedAt,
				}),
			],
		});
		leads.set(app.id, entry);
	}
	return entry;
};

// ── Сборка ответа ───────────────────────────────────────────────────────

const isOverdue = (item: StoredTask) => !item.doneAt && !!item.dueAt && Date.parse(item.dueAt) < Date.now();
const outTask = (item: StoredTask): CrmTask => ({ ...item, overdue: isOverdue(item) });
const dueMs = (item: StoredTask) => (item.dueAt ? Date.parse(item.dueAt) : Number.POSITIVE_INFINITY);
const openTasks = (entry: Lead) => entry.tasks.filter((item) => !item.doneAt).sort((a, b) => dueMs(a) - dueMs(b));
const orderedTasks = (entry: Lead) => [
	...openTasks(entry),
	...entry.tasks
		.filter((item) => item.doneAt)
		.sort((a, b) => Date.parse(b.doneAt ?? "") - Date.parse(a.doneAt ?? "")),
];
const outFile = ({ content: _content, ...rest }: StoredFile): CrmFile => ({ ...rest });

const extrasOf = (entry: Lead): CrmBoardExtras => {
	const open = openTasks(entry);
	const next = open[0];
	const last = entry.activities.map((item) => item.occurredAt).sort().at(-1) ?? null;
	return {
		source: entry.profile.source,
		tags: [...entry.profile.tags],
		nextTask: next
			? { id: next.id, title: next.title, kind: next.kind, dueAt: next.dueAt, overdue: isOverdue(next) }
			: null,
		openTasks: open.length,
		filesCount: entry.files.length,
		lastActivityAt: last,
	};
};

/** Поля CRM для строки мок-доски: доска и карточка показывают одно и то же. */
export const crmBoardExtras = (id: string): CrmBoardExtras => {
	ensureSeeded();
	const entry = leads.get(id);
	return entry ? extrasOf(entry) : {};
};

const STATUS_LABEL: Record<ApplicationStatus, string> = {
	new: "Новая",
	contacted: "В работе",
	registered: "Зарегистрирован",
	filling: "Заполняет кабинет",
	moderation: "На проверке",
	rework: "На доработке",
	active: "Активен",
	rejected: "Отклонён",
	lost: "Не дошёл",
};

/** История мок-доски пишет код стадии («Перемещена в «contacted»») — в ленте нужна стадия словами. */
const statusItem = (event: SellerApplication["history"][number]): CrmTimelineItem => {
	const code = event.message.match(/«([a-z]+)»/)?.[1] as ApplicationStatus | undefined;
	const known = code && code in STATUS_LABEL ? code : null;
	return {
		type: "status",
		at: event.at,
		fromStatus: null,
		toStatus: known ?? "",
		message: known ? event.message.replace(`«${known}»`, `«${STATUS_LABEL[known]}»`) : event.message,
		automated: event.automated,
		actor: null,
	};
};

const digits = (value?: string | null) => (value ?? "").replace(/\D/g, "");
const phoneKey = (value?: string | null) => {
	const d = digits(value);
	return d.length >= 10 ? d.slice(-10) : "";
};

const findDuplicates = (
	query: { phone?: string; email?: string; inn?: string },
	all: SellerApplication[],
	excludeId?: string,
): CrmDuplicate[] => {
	const phone = phoneKey(query.phone);
	const email = query.email?.trim().toLowerCase() ?? "";
	const inn = digits(query.inn);
	if (!phone && !email.includes("@") && inn.length < 10) return [];
	return all.flatMap((base) => {
		if (base.id === excludeId) return [];
		const entry = leads.get(base.id);
		const app = { ...base, ...entry?.overrides };
		const matchedBy: CrmDuplicate["matchedBy"] = [];
		const phones = [app.phone, ...(entry?.contacts.map((item) => item.phone) ?? [])].map(phoneKey);
		if (phone && phones.includes(phone)) matchedBy.push("phone");
		const emails = [app.email, ...(entry?.contacts.map((item) => item.email) ?? [])].map((item) =>
			(item ?? "").trim().toLowerCase(),
		);
		if (email.includes("@") && emails.includes(email)) matchedBy.push("email");
		if (inn.length >= 10 && digits(app.inn ?? entry?.profile.company.inn) === inn) matchedBy.push("inn");
		return matchedBy.length
			? [
					{
						id: app.id,
						shopName: app.shopName,
						contactName: app.contactName,
						status: app.status,
						submittedAt: app.submittedAt,
						matchedBy,
					},
				]
			: [];
	});
};

/** Доска (живая или мок) плюс заявки, заведённые в моке и на ней не видные. */
const loadAll = async () => {
	const board = await api.applications.board();
	const ids = new Set(board.applications.map((app) => app.id));
	const all = [...board.applications, ...[...created.values()].filter((app) => !ids.has(app.id))];
	return { board, all };
};

const resolve = async (id: string) => {
	const { board, all } = await loadAll();
	const base = all.find((app) => app.id === id);
	if (!base) throw new ApiError("Заявка не найдена", 404, "NOT_FOUND");
	const entry = leadFor(base);
	const app: SellerApplication = { ...base, ...entry.overrides };
	return { app, entry, board, all };
};

const accountOf = (app: SellerApplication, entry: Lead): CrmLeadAccount | null => {
	if (entry.account) return { ...entry.account };
	if (!app.userId) return null;
	return {
		userId: app.userId,
		email: app.email ?? "",
		shopId: app.userId,
		shopName: app.shopName,
		onboardingStatus: "profile_pending",
		productsCount: 0,
		publishedProductsCount: 0,
	};
};

const buildCard = (app: SellerApplication, entry: Lead, all: SellerApplication[]): ApplicationCard => {
	const tasks = orderedTasks(entry).map(outTask);
	const files = entry.files.map(outFile);
	const timeline: CrmTimelineItem[] = [
		...entry.activities.map((activity) => ({ type: "activity" as const, at: activity.occurredAt, activity: { ...activity } })),
		...app.history.map(statusItem),
		...tasks.flatMap((item) => [
			{ type: "task" as const, at: item.createdAt, action: "created" as const, task: item },
			...(item.doneAt ? [{ type: "task" as const, at: item.doneAt, action: "done" as const, task: item }] : []),
		]),
		...files.map((item) => ({ type: "file" as const, at: item.createdAt, file: item })),
	].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
	return {
		application: { ...app, ...extrasOf(entry) },
		profile: structuredClone(entry.profile),
		contacts: entry.contacts.map((item) => ({ ...item })),
		tasks,
		nextTask: tasks.find((item) => !item.doneAt) ?? null,
		files,
		timeline,
		duplicates: findDuplicates(
			{ phone: app.phone, email: app.email, inn: app.inn ?? entry.profile.company.inn },
			all,
			app.id,
		),
		account: accountOf(app, entry),
		importJobs: entry.importJobs.map((item) => ({ ...item })),
	};
};

const cardOf = async (id: string) => {
	const { app, entry, all } = await resolve(id);
	return buildCard(app, entry, all);
};

const entryOf = (id: string) => {
	ensureSeeded();
	const entry = leads.get(id);
	if (!entry) throw new ApiError("Заявка не найдена", 404, "NOT_FOUND");
	return entry;
};

const findIn = <T extends { id: string }>(list: T[], id: string, what: string): T => {
	const item = list.find((entry) => entry.id === id);
	if (!item) throw new ApiError(`${what} не найден(а)`, 404, "NOT_FOUND");
	return item;
};

/** Править и удалять касание может автор или администратор — как в шлюзе. */
const assertAuthor = (author: CrmPerson | null) => {
	const self = me();
	if (!self.isAdmin && author?.id !== self.id) {
		throw new ApiError("Править можно только свои записи", 403, "FORBIDDEN");
	}
};

/** 409 шлюза с дублями в `details`: форма показывает их и предлагает «Всё равно завести». */
class DuplicateConflict extends ApiError {
	constructor(duplicates: CrmDuplicate[]) {
		super("Похоже, такая заявка уже есть", 409, "CONFLICT", { duplicates });
	}
}

// ── DaData по ИНН ───────────────────────────────────────────────────────

const DADATA: Record<string, Omit<CrmCompany, "inn" | "filledFrom" | "filledAt">> = {
	"667012345678": {
		ogrn: "318665800012345",
		shortName: "ИП Власов Артём Сергеевич",
		fullName: "Индивидуальный предприниматель Власов Артём Сергеевич",
		orgForm: "ИП",
		address: "620014, Свердловская обл., г. Екатеринбург",
		director: "Власов Артём Сергеевич",
		state: "Действующая",
		registeredAt: "2018-06-04",
		okved: "32.12 Производство ювелирных изделий и аналогичных изделий",
	},
	"332901234561": {
		ogrn: "320332800045678",
		shortName: "ИП Мельник Игорь Олегович",
		fullName: "Индивидуальный предприниматель Мельник Игорь Олегович",
		orgForm: "ИП",
		address: "600000, Владимирская обл., г. Владимир",
		director: "Мельник Игорь Олегович",
		state: "Действующая",
		registeredAt: "2020-02-11",
		okved: "23.19 Производство и обработка прочих стеклянных изделий",
	},
};

const companyFromInn = (inn: string, app: SellerApplication): CrmCompany => {
	const fixture = DADATA[inn];
	const base: CrmCompany = fixture
		? { ...fixture }
		: inn.length === 12
			? {
					ogrn: `3${inn.slice(0, 14).padEnd(14, "0")}`,
					shortName: `ИП ${app.contactName}`,
					fullName: `Индивидуальный предприниматель ${app.contactName}`,
					orgForm: "ИП",
					address: app.city ? `г. ${app.city}` : "",
					director: app.contactName,
					state: "Действующая",
					registeredAt: "2021-04-15",
					okved: "47.91 Торговля розничная по почте или по информационно-коммуникационной сети Интернет",
				}
			: {
					kpp: `${inn.slice(0, 4)}01001`,
					ogrn: `1${inn}00`.slice(0, 13),
					shortName: `ООО «${app.shopName.toUpperCase()}»`,
					fullName: `ОБЩЕСТВО С ОГРАНИЧЕННОЙ ОТВЕТСТВЕННОСТЬЮ «${app.shopName.toUpperCase()}»`,
					orgForm: "ООО",
					address: app.city ? `г. ${app.city}, ул. Промышленная, д. 4` : "",
					director: app.contactName,
					state: "Действующая",
					registeredAt: "2019-09-02",
					okved: "47.91 Торговля розничная по почте или по информационно-коммуникационной сети Интернет",
				};
	return { ...base, inn, filledFrom: "dadata", filledAt: new Date().toISOString() };
};

// ── Заведение заявки ────────────────────────────────────────────────────

type NewLead = Omit<CrmLeadCreate, "source"> & { source: CrmLeadCreate["source"] };

const createLead = async (input: NewLead, historyMessage: string): Promise<string> => {
	const self = me();
	const { board } = await loadAll();
	const assignee =
		input.assigneeId === undefined
			? personOf(self)
			: input.assigneeId === null
				? null
				: (board.assignees.find((item) => item.id === input.assigneeId) ??
					(input.assigneeId === self.id ? personOf(self) : null));
	const nowIso = new Date().toISOString();
	const id = nextId("app_m");
	const stored = {
		id,
		shopName: input.shopName.trim(),
		contactName: input.contactName.trim(),
		city: input.city?.trim() ?? "",
		categories: input.categories ?? [],
		status: "new" as const,
		assignee,
		submittedAt: nowIso,
		statusChangedAt: nowIso,
		priority: "normal" as const,
		note: input.comment?.trim() || undefined,
		history: [{ id: nextId("evt"), at: nowIso, message: historyMessage, automated: false }],
		phone: input.phone.trim(),
		email: input.email?.trim() || undefined,
		inn: input.inn?.trim() || undefined,
	};
	mockInsertApplication(stored);
	created.set(id, {
		...stored,
		sla: {
			daysInStage: 0,
			slaDays: 3,
			overdue: false,
			daysLeft: 3,
			priority: "low",
			alerts: [],
			severity: null,
		},
	});
	ensureSeeded();
	leads.set(
		id,
		lead({
			profile: blankProfile({
				source: input.source,
				sourceDetail: input.sourceDetail?.trim() ?? "",
				company: stored.inn ? { inn: stored.inn } : {},
				links: input.links ?? {},
				tags: input.tags ?? [],
				comment: stored.note ?? "",
			}),
			contacts: [
				contact(stored.contactName, "", stored.phone, { email: stored.email, primary: true, createdAt: nowIso }),
			],
		}),
	);
	return id;
};

// ── Загрузка базы ───────────────────────────────────────────────────────

const splitList = (value: string) =>
	value
		.split(/[,;\n]/)
		.map((item) => item.trim())
		.filter(Boolean);

// ── Домен ───────────────────────────────────────────────────────────────

export const applicationCrm: Api["applicationCrm"] = {
	async card(id) {
		await sleep(120);
		return cardOf(id);
	},

	async create(input) {
		await sleep(250);
		const { all } = await loadAll();
		if (!input.allowDuplicate) {
			const duplicates = findDuplicates({ phone: input.phone, email: input.email, inn: input.inn }, all);
			if (duplicates.length) throw new DuplicateConflict(duplicates);
		}
		const sourceLabel = CRM_LEAD_SOURCE_LABELS[input.source ?? "manual"];
		const id = await createLead(
			{ ...input, source: input.source ?? "manual" },
			`Заявка заведена менеджером · ${sourceLabel}`,
		);
		return cardOf(id);
	},

	async duplicates(query) {
		await sleep(150);
		const { all } = await loadAll();
		return findDuplicates(query, all, query.excludeId);
	},

	async update(id, patch) {
		await sleep(150);
		const { entry } = await resolve(id);
		const base: Partial<SellerApplication> = {};
		if (patch.shopName !== undefined) base.shopName = patch.shopName.trim();
		if (patch.contactName !== undefined) base.contactName = patch.contactName.trim();
		if (patch.phone !== undefined) base.phone = patch.phone.trim();
		if (patch.email !== undefined) base.email = patch.email.trim();
		if (patch.city !== undefined) base.city = patch.city.trim();
		if (patch.inn !== undefined) base.inn = patch.inn.trim();
		if (patch.categories !== undefined) base.categories = patch.categories;
		if (patch.comment !== undefined) base.note = patch.comment;
		if (Object.keys(base).length) {
			entry.overrides = { ...entry.overrides, ...base };
			mockPatchApplication(id, base);
		}
		const profile = entry.profile;
		if (patch.source !== undefined) profile.source = patch.source;
		if (patch.sourceDetail !== undefined) profile.sourceDetail = patch.sourceDetail;
		if (patch.company !== undefined) {
			// Как в шлюзе: правка сливается с прежним, пустые поля выкидываются.
			const merged = { ...profile.company, ...patch.company, filledFrom: "manual", filledAt: new Date().toISOString() };
			profile.company = Object.fromEntries(Object.entries(merged).filter(([, value]) => value !== undefined && value !== ""));
		}
		if (patch.inn !== undefined) profile.company = { ...profile.company, inn: patch.inn.trim() };
		if (patch.links !== undefined) profile.links = { ...patch.links };
		if (patch.tags !== undefined) profile.tags = [...new Set(patch.tags.map((tag) => tag.trim()).filter(Boolean))];
		if (patch.assortmentSize !== undefined) profile.assortmentSize = patch.assortmentSize;
		if (patch.avgCheck !== undefined) profile.avgCheck = patch.avgCheck;
		if (patch.monthlyPotential !== undefined) profile.monthlyPotential = patch.monthlyPotential;
		if (patch.comment !== undefined) profile.comment = patch.comment;
		return cardOf(id);
	},

	async fillCompany(id) {
		await sleep(600);
		const { app, entry } = await resolve(id);
		const inn = digits(app.inn ?? entry.profile.company.inn);
		if (!inn) throw new ApiError("Сначала укажите ИНН", 400, "VALIDATION_ERROR");
		if (inn.length !== 10 && inn.length !== 12) {
			throw new ApiError("ИНН — это 10 цифр у компании или 12 у ИП", 400, "VALIDATION_ERROR");
		}
		if (/^0+$/.test(inn)) throw new ApiError("DaData не нашла компанию с таким ИНН", 404, "NOT_FOUND");
		entry.profile.company = companyFromInn(inn, app);
		return cardOf(id);
	},

	async addContact(id, input) {
		await sleep(150);
		const entry = entryOf(id);
		const item = contact(input.name.trim(), input.role?.trim() ?? "", input.phone?.trim() ?? "", {
			email: input.email?.trim(),
			telegram: input.telegram?.trim(),
			primary: input.isPrimary || entry.contacts.length === 0,
			createdAt: new Date().toISOString(),
		});
		if (item.isPrimary) for (const other of entry.contacts) other.isPrimary = false;
		entry.contacts.push(item);
		return { ...item };
	},

	async updateContact(id, contactId, patch) {
		await sleep(150);
		const entry = entryOf(id);
		const item = findIn(entry.contacts, contactId, "Контакт");
		if (patch.name !== undefined) item.name = patch.name.trim();
		if (patch.role !== undefined) item.role = patch.role.trim();
		if (patch.phone !== undefined) item.phone = patch.phone.trim();
		if (patch.email !== undefined) item.email = patch.email.trim();
		if (patch.telegram !== undefined) item.telegram = patch.telegram.trim();
		if (patch.isPrimary) for (const other of entry.contacts) other.isPrimary = other.id === item.id;
		return { ...item };
	},

	async deleteContact(id, contactId) {
		await sleep(120);
		const entry = entryOf(id);
		const item = findIn(entry.contacts, contactId, "Контакт");
		entry.contacts = entry.contacts.filter((other) => other.id !== contactId);
		const first = entry.contacts[0];
		if (item.isPrimary && first) first.isPrimary = true;
	},

	async addActivity(id, input) {
		await sleep(200);
		const { app, entry, board } = await resolve(id);
		const body = input.body?.trim() ?? "";
		const fileIds = input.fileIds ?? [];
		if (input.kind === "comment" && !body && !fileIds.length) {
			throw new ApiError("Напишите комментарий", 400, "VALIDATION_ERROR");
		}
		const self = personOf(me());
		const nowIso = new Date().toISOString();
		entry.activities.push({
			id: nextId("act"),
			kind: input.kind,
			body,
			outcome: input.outcome ?? null,
			occurredAt: input.occurredAt ?? nowIso,
			author: self,
			createdAt: nowIso,
			editedAt: null,
			fileIds,
		});
		if (input.completeTaskId) {
			const done = findIn(entry.tasks, input.completeTaskId, "Задача");
			if (!done.doneAt) {
				done.doneAt = nowIso;
				done.doneBy = self;
			}
		}
		if (input.nextTask) {
			const next = input.nextTask;
			entry.tasks.push(
				task(id, next.title.trim(), next.kind ?? "call", next.dueAt ?? null, assigneeFor(next.assigneeId, app, board.assignees), {
					createdAt: nowIso,
					createdBy: self,
				}),
			);
		}
		return cardOf(id);
	},

	async updateActivity(id, activityId, patch) {
		await sleep(150);
		const entry = entryOf(id);
		const item = findIn(entry.activities, activityId, "Касание");
		assertAuthor(item.author);
		if (patch.body !== undefined) item.body = patch.body.trim();
		if (patch.outcome !== undefined) item.outcome = patch.outcome;
		if (patch.occurredAt !== undefined) item.occurredAt = patch.occurredAt;
		item.editedAt = new Date().toISOString();
		return { ...item };
	},

	async deleteActivity(id, activityId) {
		await sleep(120);
		const entry = entryOf(id);
		const item = findIn(entry.activities, activityId, "Касание");
		assertAuthor(item.author);
		entry.activities = entry.activities.filter((other) => other.id !== activityId);
	},

	async addTask(id, input) {
		await sleep(150);
		const { app, entry, board } = await resolve(id);
		const self = personOf(me());
		const item = task(id, input.title.trim(), input.kind ?? "call", input.dueAt ?? null, assigneeFor(input.assigneeId, app, board.assignees), {
			createdAt: new Date().toISOString(),
			createdBy: self,
		});
		entry.tasks.push(item);
		return outTask(item);
	},

	async updateTask(id, taskId, patch) {
		await sleep(150);
		const { app, entry, board } = await resolve(id);
		const item = findIn(entry.tasks, taskId, "Задача");
		if (patch.title !== undefined) item.title = patch.title.trim();
		if (patch.kind !== undefined) item.kind = patch.kind;
		if (patch.dueAt !== undefined) item.dueAt = patch.dueAt;
		if (patch.assigneeId !== undefined) item.assignee = assigneeFor(patch.assigneeId, app, board.assignees);
		if (patch.done === true && !item.doneAt) {
			item.doneAt = new Date().toISOString();
			item.doneBy = personOf(me());
		}
		if (patch.done === false) {
			item.doneAt = null;
			item.doneBy = null;
		}
		return outTask(item);
	},

	async deleteTask(id, taskId) {
		await sleep(120);
		const entry = entryOf(id);
		findIn(entry.tasks, taskId, "Задача");
		entry.tasks = entry.tasks.filter((item) => item.id !== taskId);
	},

	async tasks(scope, due) {
		await sleep(150);
		ensureSeeded();
		const self = me();
		const { all } = await loadAll();
		const out: CrmTaskWithLead[] = [];
		for (const base of all) {
			const entry = leads.get(base.id);
			if (!entry) continue;
			const app = { ...base, ...entry.overrides };
			for (const item of entry.tasks) {
				if (scope === "mine" && item.assignee?.id !== self.id) continue;
				if (!matchesDue(item, due)) continue;
				out.push({
					...outTask(item),
					application: {
						id: app.id,
						shopName: app.shopName,
						contactName: app.contactName,
						status: app.status,
						phone: app.phone ?? "",
					},
				});
			}
		}
		return due === "done"
			? out.sort((a, b) => Date.parse(b.doneAt ?? "") - Date.parse(a.doneAt ?? ""))
			: out.sort((a, b) => dueMs(a) - dueMs(b));
	},

	async uploadFile(id, upload, category) {
		await sleep(300);
		if (upload.size > CRM_FILE_MAX_BYTES) throw new ApiError("Файл больше 50 МБ", 413, "PAYLOAD_TOO_LARGE");
		const { entry } = await resolve(id);
		const item = file(
			id,
			upload.name,
			upload.type || "application/octet-stream",
			category ?? "other",
			upload.size,
			new Date().toISOString(),
			personOf(me()),
			upload,
		);
		entry.files.push(item);
		return outFile(item);
	},

	async fileBlob(id, fileId) {
		await sleep(120);
		const entry = entryOf(id);
		const item = findIn(entry.files, fileId, "Файл");
		if (typeof item.content === "function") item.content = await item.content();
		return item.content;
	},

	async updateFile(id, fileId, patch) {
		await sleep(120);
		const entry = entryOf(id);
		const item = findIn(entry.files, fileId, "Файл");
		if (patch.category !== undefined) item.category = patch.category;
		if (patch.filename !== undefined) item.filename = patch.filename.trim();
		return outFile(item);
	},

	async deleteFile(id, fileId) {
		await sleep(120);
		const entry = entryOf(id);
		findIn(entry.files, fileId, "Файл");
		entry.files = entry.files.filter((item) => item.id !== fileId);
		for (const activity of entry.activities) activity.fileIds = activity.fileIds.filter((item) => item !== fileId);
	},

	async importLeads(input) {
		await sleep(input.dryRun ? 400 : 700);
		const { all } = await loadAll();
		const columns = (field: CrmImportField) =>
			input.mapping.flatMap((mapped, index) => (mapped === field ? [index] : []));
		const pick = (row: string[], field: CrmImportField) =>
			columns(field)
				.map((index) => row[index]?.trim() ?? "")
				.filter(Boolean)
				.join(", ");
		const seen = new Map<string, number>();
		const result: CrmLeadImportResult = { dryRun: input.dryRun, total: input.rows.length, created: 0, duplicates: 0, invalid: 0, rows: [] };
		for (const [index, row] of input.rows.entries()) {
			// Как в шлюзе: номер строки в `rows` с единицы, без заголовка.
			const n = index + 1;
			const shopName = pick(row, "shopName");
			const phone = pick(row, "phone");
			const email = pick(row, "email");
			const inn = pick(row, "inn");
			if (!shopName) {
				result.invalid += 1;
				result.rows.push({ row: n, status: "invalid", shopName: "", message: "Нет названия" });
				continue;
			}
			if (phoneKey(phone) === "") {
				result.invalid += 1;
				result.rows.push({ row: n, status: "invalid", shopName, message: phone ? "Телефон не похож на номер" : "Нет телефона" });
				continue;
			}
			const existing = findDuplicates({ phone, email, inn }, all)[0];
			const repeat = seen.get(phoneKey(phone));
			if (existing || repeat) {
				result.duplicates += 1;
				result.rows.push({
					row: n,
					status: "duplicate",
					shopName,
					message: existing ? `Уже есть: ${existing.shopName}` : `Повтор строки ${repeat}`,
					applicationId: existing?.id,
				});
				continue;
			}
			seen.set(phoneKey(phone), n);
			if (input.dryRun) {
				// При проверке `created` — «будет заведено».
				result.created += 1;
				result.rows.push({ row: n, status: "create", shopName });
				continue;
			}
			const links: CrmLinks = {};
			for (const key of ["website", "instagram", "vk", "telegram", "wildberries", "ozon"] as const) {
				const value = pick(row, key);
				if (value) links[key] = value;
			}
			const applicationId = await createLead(
				{
					shopName,
					contactName: pick(row, "contactName") || "Не указан",
					phone,
					email: email || undefined,
					city: pick(row, "city") || undefined,
					inn: inn || undefined,
					categories: splitList(pick(row, "categories")),
					source: input.source ?? "cold_base",
					sourceDetail: input.sourceDetail,
					comment: pick(row, "comment") || undefined,
					tags: [...new Set([...(input.tags ?? []), ...splitList(pick(row, "tags"))])],
					links,
					assigneeId: input.assigneeId,
				},
				`Заявка загружена из базы${input.sourceDetail ? ` · ${input.sourceDetail}` : ""}`,
			);
			result.created += 1;
			result.rows.push({ row: n, status: "created", shopName, applicationId });
		}
		return result;
	},

	async exportLeads() {
		await sleep(400);
		const { all } = await loadAll();
		const header = ["Магазин", "Контакт", "Телефон", "Почта", "Город", "ИНН", "Стадия", "Источник", "Теги", "Следующий шаг", "Срок шага"];
		const quote = (value: string) => (/[;"\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
		const rows = all.map((base) => {
			const entry = leads.get(base.id);
			const app = { ...base, ...entry?.overrides };
			const next = entry ? openTasks(entry)[0] : undefined;
			return [
				app.shopName,
				app.contactName,
				app.phone ?? "",
				app.email ?? "",
				app.city,
				app.inn ?? entry?.profile.company.inn ?? "",
				STATUS_LABEL[app.status] ?? app.status,
				entry ? CRM_LEAD_SOURCE_LABELS[entry.profile.source] : "",
				entry?.profile.tags.join(", ") ?? "",
				next?.title ?? "",
				next?.dueAt ? new Date(next.dueAt).toLocaleString("ru-RU") : "",
			].map(quote);
		});
		const day = new Date().toLocaleDateString("ru-RU");
		// Мок отдаёт CSV: настоящий xlsx собирает шлюз (@osmix/lib/xlsx), в админке этой зависимости нет.
		const blob = new Blob([`﻿${[header, ...rows].map((row) => row.join(";")).join("\r\n")}`], {
			type: "text/csv",
		});
		return { blob, filename: `Заявки продавцов OSMIX ${day}.csv`, rows: rows.length };
	},
};

function matchesDue(item: StoredTask, due: CrmTaskDueFilter): boolean {
	if (due === "done") return !!item.doneAt;
	if (item.doneAt) return false;
	if (due === "open") return true;
	if (due === "overdue") return isOverdue(item);
	if (!item.dueAt) return false;
	const at = Date.parse(item.dueAt);
	const today = startOfToday();
	if (due === "today") return at >= today && at < today + DAY;
	return at >= today && at < today + 8 * DAY;
}

/** Ответственный задачи: явный — из менеджеров доски, пусто — ответственный заявки, иначе автор. */
function assigneeFor(
	assigneeId: string | null | undefined,
	app: SellerApplication,
	assignees: CrmPerson[],
): CrmPerson | null {
	const self = me();
	if (assigneeId === null) return null;
	if (assigneeId === undefined) return app.assignee ?? personOf(self);
	if (assigneeId === self.id) return personOf(self);
	return assignees.find((item) => item.id === assigneeId) ?? { id: assigneeId, name: "Менеджер", initials: "М" };
}
